import { observer } from "mobx-react-lite";
import { Form as FinalForm, Field } from "react-final-form";
import { isRequired, combineValidators } from "revalidate";
import React, { useContext, useState } from "react";

// reactstrap components
import { Alert, Button, FormGroup, Form, Col, Row } from "reactstrap";
import { FORM_ERROR } from "final-form";
import TextInput from "../common/TextInput";
import { RootStoreContext } from "../../app/stores/rootStore";
import { toast } from "react-toastify";
import LogoCompany from "../common/LogoCompany";
import { useTranslation } from "react-i18next";
import { translationHookOptions } from "../../app/localization/localization";

const validateStep1 = combineValidators({
  email: isRequired("email"),
  password: isRequired("password"),
});

const validateStep2 = combineValidators({
  email: isRequired("email"),
  password: isRequired("password"),
  otp: isRequired("otp"),
});

export const LoginCustom2FactorForm = () => {
  const rootStore = useContext(RootStoreContext);
  const { loginCustomStep1, loginCustomStep2 } = rootStore.userStore;
  const { company, correctPassword } = rootStore.commonStore;
  const { showRegisterForm, showResetPwdForm } = rootStore.authStore;
  const [credentialsOk, setCredentialsOk] = useState(false);
  const [t] = useTranslation(["translation", "guest"], translationHookOptions);

  const linkToRegister = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    e.preventDefault();
    showRegisterForm();
  };

  const linkToResetPwd = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    e.preventDefault();
    showResetPwdForm();
  };

  const handleMicrophoneEditRequest = (e : any) => {
    e.preventDefault();
    
    const userPassword = window.prompt(t("insert_password"));
    if (userPassword === correctPassword) {
      window.location.href = `${window.location.origin}/player//microphone`;
    } else {
      alert(t("incorrect_password"));
    }
  };

  return (
    <div className="form-wrapper  mt-0">
      <div>
        <LogoCompany className="loginLogo" />
      </div>
      <FinalForm
        initialValues={{
          email: "",
          password: "",
          companyId: company ? company.id : 1,
        }}
        onSubmit={(values: any) =>
          credentialsOk
            ? loginCustomStep2(values).catch((error) => ({
                [FORM_ERROR]: error,
              }))
            : loginCustomStep1(values)
                .then((res) => {
                  if (res) {
                    toast.success(t("otp_sent"));
                    setCredentialsOk(true);
                  }
                })
                .catch((error) => ({
                  [FORM_ERROR]: error,
                }))
        }
        validate={credentialsOk ? validateStep2 : validateStep1}
        render={({
          handleSubmit,
          submitting,
          submitError,
          invalid,
          pristine,
          dirtySinceLastSubmit,
        }) => (
          <Form onSubmit={handleSubmit} role="form">
            {!credentialsOk && (
              <>
                <h6>{t("custom2factor_insert_credentials")}</h6>
                <FormGroup className="form-group input-group-lg">
                  <Field
                    className="form-control"
                    name="email"
                    component={TextInput}
                    placeholder={t("email_placeholder")}
                    autoComplete="email"
                  />
                </FormGroup>
                <FormGroup className="form-group input-group-lg">
                  <Field
                    className="form-control"
                    name="password"
                    component={TextInput}
                    placeholder={t("password_placeholder")}
                    type="password"
                    autoComplete="current-password"
                  />
                  <Field name="companyId" component={TextInput} type="hidden" />
                </FormGroup>
              </>
            )}

            {submitError && !dirtySinceLastSubmit && (
              <>
              {submitError.status === 400 ? (
                <Alert color="danger">{t("invalid_claims")}</Alert>
              ) : (
              <Alert color="danger">{t("invalid_credentials")}</Alert>
              )}
              </>
            )}

            {credentialsOk && (
              <>
                <p className="text-muted">{t("custom2factor_insert_otp")}</p>{" "}
                <FormGroup className="form-group input-group-lg">
                  <Field
                    name="otp"
                    component={TextInput}
                    placeholder={t("otp_placeholder")}
                    autoComplete="off"
                    type="number"
                    className="form-control"
                  />
                </FormGroup>
                <div className="text-center text-muted mb-4">
                  <a
                    className="text-muted text-underline"
                    href="/#"
                    onClick={(e) => setCredentialsOk(false)}
                  >
                    <u>{t("custom2factor_retry")}</u>
                  </a>
                </div>
              </>
            )}

            <div className="text-center">
              <Button
                disabled={(invalid && !dirtySinceLastSubmit) || pristine}
                className="btn loginButton btn-block btn-lg"
                color="dark"
                type="submit"
              >
                {t("login")}
              </Button>
            </div>
          </Form>
        )}
      />
      <Row className="mt-3">
      <Col style={{paddingRight:"0px"}}>
          <p className="text-muted">{t("forgot_password")}</p>
          <a
            className="btn btn-dark loginButton text-white btn-outline-light btn-sm"
            href="/#"
            onClick={(e) => linkToResetPwd(e)}
          >
            {t("click_here")}
          </a>
        </Col>
        {company && company.allowRegistration && (
          // <Col>
          //   <p className="text-muted">{t("registration_text")}</p>
          //   <a
          //     className="btn btn-dark loginButton text-white btn-outline-light btn-sm"
          //     href="/#"
          //     onClick={(e) => linkToRegister(e)}
          //   >
          //     {t("registration_link")}
          //   </a>
          // </Col>
          <Col>
          <p className="text-muted">{t("registration_text")}</p>
          <a
            className="btn btn-dark loginButton text-white btn-outline-light btn-sm"
            href="/#"
            onClick={(e) => linkToRegister(e)}
          >
            {t("registration_link")}
          </a>
        </Col>
        )}
      </Row>
      {company && company.allowGuest && (
        <Row className="mt-3">
          <Col>
            <a className="text-muted text-underline" href="/guest">
              <u>{t("guest_login_link", { ns: "guest" })}</u>
            </a>
          </Col>
        </Row>
      )}
      {company && company.allowBroadcast && (
        <Col className="text-center" xs="12" style={{marginTop: "10px"}}>
          <a className="text-center text-underline text-muted" href={`${window.location.pathname}//microphone`} onClick={handleMicrophoneEditRequest}>
            <u>{t("microphone_link")}</u>
          </a>
        </Col>
      )}
    </div>
  );
};

export default observer(LoginCustom2FactorForm);
